import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import { useAuth } from '../contexts/AuthContext';
import {
  type EmployeeProjectModel,
  employeeProjectModelService,
} from '../services/employeeProjectModelService';
import { projectReportSettingService } from '../services/projectReportSettingService';
import { projectService } from '../services/projectService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { findAmbiguousModelIds } from '../utils/employeeProjectModelOverlap';
import { EmployeeProjectModelDrawer } from './EmployeeProjectModelDrawer';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

const WORK_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  office: { label: 'Office', className: 'bg-primary/20 text-primary' },
  site: { label: 'Site', className: 'bg-warning/20 text-warning' },
  both: { label: 'Both', className: 'bg-success/20 text-success' },
};

const WorkTypeCellRenderer = (params: { value?: string }) => {
  const workType = params.value?.toLowerCase();
  if (!workType) return 'N/A';
  const config = WORK_TYPE_BADGE[workType];
  if (!config) {
    return (
      <Badge variant="outline" className="capitalize">
        {params.value}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

interface EmployeeProjectModelListProps {
  selectedProjectId?: string;
  onProjectChange?: (projectId: string) => void;
  onEdit?: (model: EmployeeProjectModel) => void;
  onDelete?: (modelId: string) => void;
  onAdd?: () => void;
}

export const EmployeeProjectModelList: React.FC<EmployeeProjectModelListProps> = ({
  selectedProjectId: propSelectedProjectId,
  onProjectChange,
  onEdit,
  onDelete,
  onAdd,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [_selectedModels, setSelectedModels] = useState<EmployeeProjectModel[]>([]);
  const [searchTerm] = useState('');
  const [internalSelectedProjectId, setInternalSelectedProjectId] = useState<string>('');
  const selectedProjectId =
    propSelectedProjectId !== undefined ? propSelectedProjectId : internalSelectedProjectId;
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedModel, setSelectedModel] = useState<EmployeeProjectModel | null>(null);
  const [editingModel, setEditingModel] = useState<EmployeeProjectModel | null>(null);
  const [includeTravelTime, setIncludeTravelTime] = useState<boolean>(false);
  const [showIsolatedSummary, setShowIsolatedSummary] = useState<boolean>(false);
  const [savedIncludeTravelTime, setSavedIncludeTravelTime] = useState<boolean>(false);
  const [savedShowIsolatedSummary, setSavedShowIsolatedSummary] = useState<boolean>(false);
  const [isLoadingSetting, setIsLoadingSetting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const isAdmin = userRole === 'admin';

  const {
    data: models = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['employee-project-models'],
    queryFn: () => employeeProjectModelService.getEmployeeProjectModels(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const { data: projects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects', 'by-role'],
    queryFn: () => (isAdmin ? projectService.getProjects() : projectService.getProjectsByRole()),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const externalProjects = useMemo(() => {
    return projects.filter((p) => p.type === 'External');
  }, [projects]);

  const selectedProject = useMemo(() => {
    return externalProjects.find((p) => p.id === selectedProjectId);
  }, [externalProjects, selectedProjectId]);

  useEffect(() => {
    if (externalProjects.length > 0 && propSelectedProjectId === undefined) {
      if (
        !internalSelectedProjectId ||
        !externalProjects.some((p) => p.id === internalSelectedProjectId)
      ) {
        setInternalSelectedProjectId(externalProjects[0].id);
      }
    }
  }, [externalProjects, internalSelectedProjectId, propSelectedProjectId]);

  useEffect(() => {
    if (selectedProject?.project_number) {
      setIsLoadingSetting(true);
      projectReportSettingService
        .getSetting(selectedProject.project_number)
        .then((setting) => {
          setIncludeTravelTime(setting.include_travel_time);
          setShowIsolatedSummary(setting.show_isolated_summary);
          setSavedIncludeTravelTime(setting.include_travel_time);
          setSavedShowIsolatedSummary(setting.show_isolated_summary);
        })
        .catch((error) => {
          console.error('Failed to load project report setting:', error);
          setIncludeTravelTime(false);
          setShowIsolatedSummary(false);
          setSavedIncludeTravelTime(false);
          setSavedShowIsolatedSummary(false);
        })
        .finally(() => {
          setIsLoadingSetting(false);
        });
    }
  }, [selectedProject?.project_number]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      includeTravelTime !== savedIncludeTravelTime ||
      showIsolatedSummary !== savedShowIsolatedSummary
    );
  }, [includeTravelTime, showIsolatedSummary, savedIncludeTravelTime, savedShowIsolatedSummary]);

  const updateSettingMutation = useMutation({
    mutationFn: async ({
      includeTravelTime,
      showIsolatedSummary,
    }: {
      includeTravelTime?: boolean;
      showIsolatedSummary?: boolean;
    }) => {
      if (!selectedProject?.project_number) {
        throw new Error('No project selected');
      }
      return projectReportSettingService.updateSetting(
        selectedProject.project_number,
        includeTravelTime,
        showIsolatedSummary
      );
    },
    onSuccess: (data) => {
      setIncludeTravelTime(data.include_travel_time);
      setShowIsolatedSummary(data.show_isolated_summary);
      setSavedIncludeTravelTime(data.include_travel_time);
      setSavedShowIsolatedSummary(data.show_isolated_summary);
    },
  });

  const handleIncludeTravelTimeChange = useCallback((checked: boolean) => {
    setIncludeTravelTime(checked);
  }, []);

  const handleShowIsolatedSummaryChange = useCallback((checked: boolean) => {
    setShowIsolatedSummary(checked);
  }, []);

  const handleSaveSettings = useCallback(() => {
    if (!hasUnsavedChanges) return;

    const confirmed = window.confirm(
      'Are you sure you want to save the project report settings? This will update the settings for this project.'
    );

    if (confirmed) {
      updateSettingMutation.mutate({
        includeTravelTime,
        showIsolatedSummary,
      });
    }
  }, [hasUnsavedChanges, includeTravelTime, showIsolatedSummary, updateSettingMutation]);

  const handleCancelSettings = useCallback(() => {
    setIncludeTravelTime(savedIncludeTravelTime);
    setShowIsolatedSummary(savedShowIsolatedSummary);
  }, [savedIncludeTravelTime, savedShowIsolatedSummary]);

  const handleAddClick = useCallback(() => {
    setEditingModel(null);
    setPanelMode('add');
  }, []);

  const handleFormClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingModel(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingModel(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedModel(null);
    setEditingModel(null);
  }, []);

  const handleRowClicked = useCallback((event: RowClickedEvent<EmployeeProjectModel>) => {
    if (!event.data) return;
    setSelectedModel(event.data);
    setPanelMode('selected');
    setEditingModel(null);
  }, []);

  const handleDrawerSave = useCallback(
    async (modelData: Partial<EmployeeProjectModel>) => {
      justSavedRef.current = true;
      try {
        let savedModel: EmployeeProjectModel;
        if (panelMode === 'edit' && editingModel) {
          savedModel = await employeeProjectModelService.updateEmployeeProjectModel(
            editingModel.id,
            modelData
          );
          if (onEdit) {
            onEdit(savedModel);
          }
        } else {
          savedModel = await employeeProjectModelService.createEmployeeProjectModel(modelData);
          if (onAdd) {
            onAdd();
          }
        }
        queryClient.invalidateQueries({ queryKey: ['employee-project-models'] });
        justSavedRef.current = true;
        setPanelMode('empty');
        setSelectedModel(null);
        setEditingModel(null);
      } catch (error) {
        justSavedRef.current = false;
        console.error('Failed to save employee project model:', error);
      }
    },
    [onEdit, onAdd, panelMode, editingModel, queryClient]
  );

  const handleDelete = useCallback(
    async (model: EmployeeProjectModel) => {
      if (
        !window.confirm(
          'Are you sure you want to delete this employee project model? This action cannot be undone.'
        )
      ) {
        return;
      }
      try {
        await employeeProjectModelService.deleteEmployeeProjectModel(model.id);
        if (onDelete) {
          onDelete(model.id);
        }
        queryClient.invalidateQueries({ queryKey: ['employee-project-models'] });
        setSelectedModel(null);
        setPanelMode('empty');
      } catch (error) {
        console.error('Failed to delete employee project model:', error);
      }
    },
    [onDelete, queryClient]
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Employee',
        field: 'employee',
        flex: 1,
        minWidth: 200,
        maxWidth: 300,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        valueGetter: (params: any) => {
          const employee = params.data?.employee;
          if (!employee) return 'N/A';
          return employee.fullname || employee.employee_id || 'N/A';
        },
        filterValueGetter: (params: any) => {
          const employee = params.data?.employee;
          if (!employee) return 'N/A';
          return employee.fullname || employee.employee_id || 'N/A';
        },
        cellRenderer: (params: any) => {
          const employee = params.data?.employee;
          if (!employee) return 'N/A';
          return employee.fullname || employee.employee_id || 'N/A';
        },
      },
      {
        headerName: 'Order',
        field: 'order',
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.value || params.value.trim() === '') return 'N/A';
          return params.value;
        },
      },
      {
        headerName: 'Sales Rate',
        field: 'sales_rate',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agNumberColumnFilter',
        filterParams: {
          allowedCharPattern: '\\d\\-\\,',

          numberParser: (text: string) => {
            return text == null ? null : parseFloat(text);
          },
        },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `€${params.value.toFixed(2)}`;
        },
      },
      {
        headerName: 'Work Type',
        field: 'work_type',
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellRenderer: WorkTypeCellRenderer,
        valueFormatter: (params: any) => {
          if (!params.value) return 'N/A';
          const config = WORK_TYPE_BADGE[params.value.toLowerCase()];
          return config?.label ?? params.value;
        },
      },
      {
        headerName: 'Start Date',
        field: 'start_date',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            } else {
              return 0;
            }
          },
        },
        valueFormatter: (params: any) => {
          if (!params.value) return 'N/A';
          return formatDateDdMmYyyy(params.value);
        },
      },
      {
        headerName: 'End Date',
        field: 'end_date',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (!cellValue) return -1;
            if (cellValue === 'No end date') return 1;
            const cellDate = new Date(cellValue);
            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            } else {
              return 0;
            }
          },
        },
        valueFormatter: (params: any) => {
          if (!params.value) return 'N/A';
          // Sentinel used by the filter comparator; not a parseable date.
          if (params.value === 'No end date') return params.value;
          return formatDateDdMmYyyy(params.value);
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          const status = params.value || 'active';
          const statusClass =
            status === 'active'
              ? 'bg-success-500/20 text-success-600'
              : status === 'archived'
                ? 'bg-muted text-text-secondary'
                : 'bg-muted text-text-muted';

          return (
            <span className={`rounded px-2 py-1 text-xs font-semibold capitalize ${statusClass}`}>
              {status}
            </span>
          );
        },
      },
    ],
    []
  );

  const handleProjectFilterChange = useCallback(
    (projectId: string) => {
      if (onProjectChange) {
        onProjectChange(projectId);
      } else {
        setInternalSelectedProjectId(projectId);
      }
    },
    [onProjectChange]
  );

  const filteredModels = useMemo(() => {
    if (!selectedProjectId) return [];
    return models.filter((model) => model.project_id === selectedProjectId);
  }, [models, selectedProjectId]);

  const ambiguousModelIds = useMemo(() => findAmbiguousModelIds(filteredModels), [filteredModels]);

  const getRowStyle = useCallback(
    (params: { data?: EmployeeProjectModel }) => {
      const id = params.data?.id;
      if (id && ambiguousModelIds.has(id)) {
        return { backgroundColor: '#fee2e2' };
      }
      return undefined;
    },
    [ambiguousModelIds]
  );

  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'employee-project-models.csv',
        columnKeys: [
          'employee',
          'sales_rate',
          'work_type',
          'order',
          'start_date',
          'end_date',
          'status',
        ],
      });
    }
  }, [gridApi]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedModels(selectedData);
    }
  }, [gridApi]);

  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    rowNumbers: { width: 30, minWidth: 25 },
  };

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          <EmployeeProjectModelDrawer
            key={editingModel?.id ?? 'add'}
            isOpen={true}
            onClose={handleFormClose}
            onSave={handleDrawerSave}
            model={editingModel}
            isEdit={panelMode === 'edit'}
            projectId={selectedProjectId}
          />
        ) : panelMode === 'selected' && selectedModel ? (
          <EmployeeProjectModelDrawer
            key={selectedModel.id}
            isOpen={true}
            readOnly
            model={selectedModel}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={() => {
              setEditingModel(selectedModel);
              setPanelMode('edit');
            }}
            onDeleteRequest={onDelete ? () => void handleDelete(selectedModel) : undefined}
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col p-2">
          <div className="mb-2 flex shrink-0 items-center justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectFilterChange(e.target.value)}
                  className="w-full cursor-pointer rounded-md border border-border-default bg-white p-2 text-sm text-text-primary disabled:cursor-not-allowed"
                  disabled={isLoadingProjects || externalProjects.length === 0}
                >
                  {isLoadingProjects ? (
                    <option value="">Loading projects...</option>
                  ) : externalProjects.length === 0 ? (
                    <option value="">No projects available</option>
                  ) : (
                    externalProjects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.project_number} - {project.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>

            <Button type="button" onClick={handleAddClick}>
              <PlusIcon data-icon="inline-start" />
              Add Project Member
            </Button>
          </div>
          {selectedProject && (
            <div className="mb-2 flex h-[50px] items-center gap-20">
              <div className="flex gap-3">
                <label
                  className={`flex items-center gap-2 text-sm text-text-secondary ${isLoadingSetting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    checked={includeTravelTime}
                    onChange={(e) => handleIncludeTravelTimeChange(e.target.checked)}
                    disabled={isLoadingSetting}
                    className="size-4 disabled:cursor-not-allowed"
                  />
                  Invoice Travel time by Sales Rate
                </label>
                <label
                  className={`flex items-center gap-2 text-sm text-text-secondary ${isLoadingSetting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    checked={showIsolatedSummary}
                    onChange={(e) => handleShowIsolatedSummaryChange(e.target.checked)}
                    disabled={isLoadingSetting}
                    className="size-4 disabled:cursor-not-allowed"
                  />
                  Show summary in isolated report
                </label>
              </div>
              {hasUnsavedChanges && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-green-500 text-white hover:bg-success-600"
                    onClick={handleSaveSettings}
                    disabled={updateSettingMutation.isPending}
                  >
                    {updateSettingMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleCancelSettings}
                    disabled={updateSettingMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <span className="text-xs italic text-text-secondary">
                    You have unsaved changes
                  </span>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md border border-danger-500/30 bg-danger-500/10 p-4 text-danger-600">
              Error loading employee project models:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="ml-4"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          <style>{agGridThemeStyles}</style>
          <style>{`
        .ag-theme-quartz .ag-header-cell .ag-header-cell-filter-button {
          display: block !important;
        }
        .ag-theme-quartz .ag-header-cell .ag-icon-filter {
          display: block !important;
        }
      `}</style>
          {filteredModels.length > 0 && (
            <div className="ag-theme-material min-h-0 flex-1" style={agGridSettingsContainerStyles}>
              {ambiguousModelIds.size > 0 && (
                <div className="mb-2 rounded-md border border-danger-500/30 bg-danger-500/10 px-3 py-2 text-sm text-text-primary">
                  Highlighted rows overlap another row for the same employee and order with a
                  conflicting work type (e.g. both vs office or site) or sales rate. Overlapping
                  office and site, or different order numbers, are allowed — adjust other cases so
                  one rate applies per day.
                </div>
              )}
              <AgGridReact
                ref={gridRef}
                rowData={filteredModels}
                columnDefs={columnDefs}
                defaultColDef={agGridDefaultColDef}
                gridOptions={gridOptions}
                getRowStyle={getRowStyle}
                getRowId={(params) => params.data.id}
                animateRows={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
              />

              {isLoading && (
                <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg bg-surface-card px-8 py-4 text-sm font-medium text-text-secondary shadow-md">
                  <Spinner className="size-4" />
                  Loading employee project models...
                </div>
              )}
            </div>
          )}

          {filteredModels.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">👥</div>
              <div className="text-lg font-medium text-text-secondary">
                No employee project models found
              </div>
              <div className="text-sm text-text-muted">
                Add your first employee project model to get started
              </div>
              <Button type="button" className="mt-2" onClick={handleAddClick}>
                <PlusIcon data-icon="inline-start" />
                Add Employee Project Model
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};
